import React, { useState, useEffect } from 'react';
import logo from './logo.svg';
import './globals.css';
import './output.css';
import Spreadsheet from "./components/spreadsheet"
import LoginCard from './components/login';
import Account from './components/account';
import Users from './components/users';
import Files from './components/files';
import { Button } from "./components/ui/button";
import axios, { Axios } from 'axios';
import { log } from 'console';
import { SidebarProvider, SidebarTrigger } from './components/ui/sidebar';
import { AppSidebar } from './components/ui/app-sidebar';
import EULAComponent from './components/eula-component';
import SearchSheet from './components/searchSheet';
import SearchMenu from './components/searchMenu';
import AmicusInfoDialog from './components/amicusInfoDialog';
import MatterDetail from './components/matterDetail';
import RecentMatters from './components/recentMatters';
import { AmicusFile } from './lib/conflix';
import { checkHasFilesFeature, checkHasProFeature } from "./lib/utils";
import { Alert, AlertDescription, AlertTitle } from "./components/ui/alert";

function App() {
  const [loggedIn, setLoggedIn] = useState<boolean>(() => {
    const stored = sessionStorage.getItem("isAuthenticated");
    return stored ? JSON.parse(stored) : false;
  });

  const [idToken, setIdToken] = useState<string>(() => {
    const stored = sessionStorage.getItem("idToken");
    return stored ? stored : "";
  });

  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [amicusFile, setAmicusFile] = useState<AmicusFile | null>(null);    
  const [hasPro, setHasPro] = useState<boolean>(false);
  const [hasFiles, setHasFiles] = useState<boolean>(false);
  const [showProAlert, setShowProAlert] = useState<boolean>(false);
  const [showHiddenAlert, setShowHiddenAlert] = useState<boolean>(false);

  const [currentView, setCurrentView] = useState<string>("home");

  useEffect(() => {
    sessionStorage.setItem("isAuthenticated", JSON.stringify(loggedIn));
    sessionStorage.setItem("idToken", JSON.stringify(idToken));
  }, [loggedIn]);

  useEffect(() => {
    sessionStorage.setItem("idToken", idToken)
  }, [idToken]);

  // Check for features access
  useEffect(() => {
    const checkFeaturesStatus = async () => {
      if (loggedIn && idToken) {
        const apiUrl = process.env.REACT_APP_API_URL;
        if (apiUrl) {
          const hasFilesAccess = await checkHasFilesFeature(apiUrl, idToken);
          setHasFiles(hasFilesAccess);

           const hasProAccess = await checkHasProFeature(apiUrl, idToken);
          setHasPro(hasProAccess);
        }
      }
    };
    
    checkFeaturesStatus();
  }, [loggedIn, idToken]);

  // Listen for URL changes to handle navigation
  useEffect(() => {
    const handleNavigation = async () => {
      const path = window.location.pathname;
      
      if (path.includes('account')) {
        setCurrentView('account');
      } else if (path.includes('users')) {
        setCurrentView('users');
      } else if (path.includes('search')) {
        setCurrentView('search');
      } else if (path.includes('matters/recent')) {
        // Check if user has pro access before showing recent matters
        if (hasPro) {
          setCurrentView('recentMatters');
        } else {
          // Redirect to home if no pro access
          window.history.pushState({}, '', '/');
          setCurrentView('home');
          // Show pro access required notification
          setShowProAlert(true);
          // Auto-hide alert after 5 seconds
          setTimeout(() => setShowProAlert(false), 5000);
        }
      } else if (path.includes('matter/')) {
        // Check if user has pro access before showing matter details
        if (hasPro) {
          setCurrentView('matterDetail');
        } else {
          // Redirect to home if no pro access
          window.history.pushState({}, '', '/');
          setCurrentView('home');
          // Show pro access required notification
          setShowProAlert(true);
          // Auto-hide alert after 5 seconds
          setTimeout(() => setShowProAlert(false), 5000);
        }
      } else if (path.includes('files')) {
        if (hasFiles) {
          setCurrentView('files');
        } else {
          // Redirect to home if no files access
          window.history.pushState({}, '', '/');
          setCurrentView('home');
          // Show files access required notification
          setShowHiddenAlert(true);
          // Auto-hide alert after 5 seconds
          setTimeout(() => setShowHiddenAlert(false), 5000);
        }
      } else {
        setCurrentView('home');
      }
    };

    window.addEventListener('popstate', handleNavigation);
    handleNavigation(); // Initial check

    return () => {
      window.removeEventListener('popstate', handleNavigation);
    };
  }, [hasPro, hasFiles]);

  const handleSidebarNavigation = (url: string) => {
    if (url === '/account') {
      setCurrentView('account');
      window.history.pushState({}, '', '/account');
    } else if (url === '/users') {
      setCurrentView('users');
      window.history.pushState({}, '', '/users');
    } else if (url === '/search') {
      setCurrentView('search');
      window.history.pushState({}, '', '/search');
    } else if (url === '/matters/recent') {
      if (hasPro) {
        setCurrentView('recentMatters');
        window.history.pushState({}, '', '/matters/recent');
      } else {
        // Stay on current page if no pro access
        // Show pro access required notification
        setShowProAlert(true);
        // Auto-hide alert after 5 seconds
        setTimeout(() => setShowProAlert(false), 5000);
      }
    } else if (url === '/files') {
      setCurrentView('files');
      window.history.pushState({}, '', '/files');
    } else {
      setCurrentView('home');
      window.history.pushState({}, '', '/');
    }
  };

  const renderContent = () => {
    if (!loggedIn) {
      return <LoginCard onLogin={ (id : string) => { setLoggedIn(loggedIn => true); setIdToken(idToken => id); } }/>
    } else {
      return (
        <SidebarProvider>
          <AppSidebar
            hasPro={hasPro}
            hasFiles={hasFiles}
            logout={() => { setLoggedIn(loggedIn => false); setIdToken(""); return Promise.resolve() }}
          />
          <div className="w-full max-w-full overflow-auto">
            <SidebarTrigger />
            {currentView === 'account' ? (
              <Account idToken={idToken} />
            ) : currentView === 'users' ? (
              <Users idToken={idToken}/>
            ) : currentView === 'search' ? (
              <SearchMenu idToken={idToken} setDialogOpen={setOpenDialog} setAmicusFile={setAmicusFile} /> 
            ) : currentView === 'matterDetail' ? (
              <MatterDetail idToken={idToken} />
            ) : currentView === 'recentMatters' ? (
              <RecentMatters idToken={idToken} />
            ) : currentView === 'files' ? (
              <Files idToken={idToken} />
            ) : (
              <Spreadsheet hasPro={hasPro} idToken={idToken} />
            )}
            <AmicusInfoDialog amicusFile={amicusFile} dialogOpen={openDialog} setDialogOpen={setOpenDialog} />
          </div>
        </SidebarProvider>
      );
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      {showProAlert && (
        <div className="fixed top-4 right-4 z-50 max-w-md">
          <Alert className="border-amber-500 bg-amber-50">
            <AlertTitle className="text-amber-800">Pro subscription required</AlertTitle>
            <AlertDescription className="text-amber-700">
              Access to matter details and recent matters requires a Pro subscription. Please upgrade your account to access these features.
            </AlertDescription>
          </Alert>
        </div>
      )}
      {showHiddenAlert && (
        <div className="fixed top-4 right-4 z-50 max-w-md">
          <Alert className="border-amber-500 bg-amber-50">
            <AlertTitle className="text-amber-800">Tab Unavailable</AlertTitle>
            <AlertDescription className="text-amber-700">
              This tab is currently unavailable.
            </AlertDescription>
          </Alert>
        </div>
      )}
      {renderContent()}
      <EULAComponent />
    </div>
  );
}

export default App;
